import React, {useEffect, useMemo, useState} from "react";
import "./landing.css";
import { useNavigate, useParams } from "react-router-dom";
import Select from "../../ui/Select";
import logo from "../../images/logo.webp";
import { BOT_URL, LANDING_CONFIG } from "./landingConfig";
import appStore from "../../storage/AppStore";
import {HtmlDiv} from '../../common/utils'

function getScrollParent(node) {
  let parent = node?.parentElement;

  while (parent) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY;
    const canScroll =
      (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
      parent.scrollHeight > parent.clientHeight;

    if (canScroll) return parent;
    parent = parent.parentElement;
  }

  return document.scrollingElement || document.documentElement;
}

function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;

  const header = document.querySelector(".lnd-nav");
  const headerHeight = header ? header.offsetHeight : 0;

  const scroller = getScrollParent(el);

  const isWindowScroller =
    scroller === document.documentElement ||
    scroller === document.body ||
    scroller === document.scrollingElement;

  const elRect = el.getBoundingClientRect();
  const scrollerRect = isWindowScroller
    ? { top: 0 }
    : scroller.getBoundingClientRect();

  const currentScrollTop = isWindowScroller ? window.pageYOffset : scroller.scrollTop;

  const targetTop =
    elRect.top - scrollerRect.top + currentScrollTop - headerHeight - 12;

  if (isWindowScroller) {
    window.scrollTo({
      top: targetTop,
      behavior: "smooth",
    });
  } else {
    scroller.scrollTo({
      top: targetTop,
      behavior: "smooth",
    });
  }
}

function openBot() {
  window.open(BOT_URL, "_blank", "noopener,noreferrer");
}

// Country is selected independently of UI language. The server-side checkout will decide the charge currency.
const PRICE_COUNTRIES = {
  OTHER: {currency: "USD", locale: "en-US", label: "🌐 USD"},
  KG: {currency: "KGS", locale: "ru-KG", label: "🇰🇬 KGS"},
  RU: {currency: "RUB", locale: "ru-RU", label: "🇷🇺 RUB"},
  KZ: {currency: "KZT", locale: "kk-KZ", label: "🇰🇿 KZT"},
  UZ: {currency: "UZS", locale: "uz-UZ", label: "🇺🇿 UZS"},
};

function ScreenCard({ title, src, alt }) {
  if (!src) return null;

  return (
    <div className="lnd-screen-card">
      {title ? <div className="lnd-screen-card__title">{title}</div> : null}
      <img
        src={src}
        className="lnd-screen-img"
        alt={alt || title || "screen"}
      />
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const params = useParams();
  const [menuOpen, setMenuOpen] = useState(false);

  const lang = useMemo(() => {
    const code = params?.lang || appStore.user.language || "ru";
    return LANDING_CONFIG[code] ? code : "ru";
  }, [params?.lang]);

  const content = LANDING_CONFIG[lang] || LANDING_CONFIG.ru;

  useEffect(() => {
    document.body.classList.add("landing-page-body");
    document.documentElement.classList.add("landing-page-html");

    return () => {
      document.body.classList.remove("landing-page-body");
      document.documentElement.classList.remove("landing-page-html");
    };
  }, []);

  const closeMenu = () => setMenuOpen(false);

  const handleLanguageChange = (val) => {
    const nextLang = typeof val === "string" ? val : val?.id || "ru";
    closeMenu();
    navigate(`/demo/${nextLang}`);
  };

  const handleNavClick = (id) => {
    closeMenu();
    setTimeout(() => {
      scrollToId(id);
    }, 260);
  };

  const analyticsStats = content?.analytics?.stats || [];
  const pricingPlans = content?.pricing?.plans || [];
  const [priceCountry, setPriceCountry] = useState(() => {
    const country = String(appStore?.user?.country_id || appStore?.user?.country?.id || "").toUpperCase();
    return ["KG", "RU", "KZ", "UZ"].includes(country) ? country : "OTHER";
  });
  const [usdRates, setUsdRates] = useState(null);
  const [ratesPending, setRatesPending] = useState(false);
  const priceCurrency = PRICE_COUNTRIES[priceCountry]?.currency || "USD";

  useEffect(() => {
    if (priceCurrency === "USD" || usdRates?.[priceCurrency]) return;
    let active = true;
    setRatesPending(true);
    fetch("https://open.er-api.com/v6/latest/USD")
      .then((response) => { if (!response.ok) throw new Error("rate_fetch_failed"); return response.json(); })
      .then((data) => {
        if (active && data.result === "success" && data.base_code === "USD" && data.rates) setUsdRates(data.rates);
      })
      .catch((error) => { if (active) console.warn("--landing exchange rates unavailable", error); })
      .finally(() => { if (active) setRatesPending(false); });
    return () => { active = false; };
  }, [priceCurrency, usdRates]);

  const formatLocalPrice = (priceUsd) => {
    const rate = usdRates?.[priceCurrency];
    if (priceCurrency === "USD" || !Number.isFinite(rate) || rate <= 0 || !priceUsd) return null;
    return new Intl.NumberFormat(PRICE_COUNTRIES[priceCountry].locale, {style: "currency", currency: priceCurrency, maximumFractionDigits: 0}).format(priceUsd * rate);
  };
  const showLocalPrice = (priceUsd) => {
    const formatted = formatLocalPrice(priceUsd);
    if (!formatted) return null;
    return <div className="lnd-local-price">≈ {formatted}</div>;
  };

  const selectedLanguageTemplate = (option, props) => {
        if (option) {
            return (
                  <span className="text-lg">{option.icon}</span>
            );
        }

        return <span>{props.placeholder}</span>;
    };

  const languageOptionTemplate = (option, props) => {
        if (option) {
            return (
              <div className="flex align-items-center">
                  <div>{option.icon} {option.label}</div>
              </div>
            );
        }

        return <span>{props.placeholder}</span>;
    };

  return (
    <div className="lnd">
      <header className="lnd-nav">
        <div className="lnd-container lnd-nav__inner">
          <div
            className="lnd-brand"
            role="button"
            tabIndex={0}
            onClick={() => navigate("/")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") navigate("/");
            }}
          >
            <img src={logo} className="lnd-logo" alt="Club #1" />
            <div className="lnd-brand__text">
              <div className="lnd-brand__name">Club #1</div>
            </div>
          </div>

          <nav className="lnd-links" aria-label="Навигация">
            {(content.nav || []).map((item) => (
              <button
                key={item.id}
                type="button"
                className="lnd-link"
                onClick={() => handleNavClick(item.id)}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className={`lnd-nav__cta ${menuOpen ? "lnd-nav__cta--menu-open" : ""}`}>
            <div className="lnd-lang-select">
              <Select options={appStore.languages} value={lang}
                      label={appStore.tr.language} valueTemplate={selectedLanguageTemplate}
                      itemTemplate={languageOptionTemplate}
                      onChange={(val) => handleLanguageChange(val)}
                      className={"lang-select"}
              />
            </div>

            <button
              type="button"
              className={`lnd-burger ${menuOpen ? "is-open" : ""}`}
              aria-label="Открыть меню"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <button
          type="button"
          className="lnd-mobile-menu__backdrop"
          aria-label="Закрыть меню"
          onClick={closeMenu}
        />
      )}

      <aside className={`lnd-mobile-menu ${menuOpen ? "is-open" : ""}`}>
        <div className="lnd-mobile-menu__inner">
          <div className="lnd-mobile-menu__top">
            <div className="lnd-mobile-menu__title">Меню</div>
            <button
              type="button"
              className="lnd-mobile-menu__close"
              onClick={closeMenu}
              aria-label="Закрыть меню"
            >
              ×
            </button>
          </div>

          <div className="lnd-mobile-menu__links">
            {(content.nav || []).map((item) => (
              <button
                key={item.id}
                type="button"
                className="lnd-mobile-link"
                onClick={() => handleNavClick(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </aside>

      <section className="lnd-hero">
        <div className="lnd-container lnd-hero__grid">
          <div className="lnd-hero__left">
            <div className="lnd-kicker">{content.hero?.kicker}</div>
            <h1 className="lnd-h1">{content.hero?.title}</h1>

            {(content.hero?.paragraphs || []).map((p) => (
              <p key={p} className="lnd-p">
                {p}
              </p>
            ))}

            <div className="lnd-actions">
              <button
                type="button"
                className="lnd-btn lnd-btn--primary"
                onClick={openBot}
              >
                {content.hero?.button}
              </button>
            </div>

            <div className="lnd-badges">
              {(content.hero?.pills || []).map((pill) => (
                <span key={pill} className="lnd-pill">
                  {pill}
                </span>
              ))}
            </div>
          </div>

          <div className="lnd-hero__right">
            {content.hero?.image ? (
              <ScreenCard
                title={content.hero.image.title}
                src={content.hero.image.src}
                alt={content.hero.image.alt}
              />
            ) : null}
          </div>
        </div>
      </section>

      <section id="modules" className="lnd-section">
        <div className="lnd-container">
          <div className="lnd-kicker">{content.modules?.kicker}</div>
          <h2 className="lnd-h2">{content.modules?.title}</h2>
          <p className="lnd-p">{content.modules?.text}</p>

          <div className="lnd-grid lnd-grid--3 lnd-mt">
            {(content.modules?.cards || []).map((m) => (
              <div key={m.title} className="lnd-feat">
                <div className="lnd-feat__icon" aria-hidden="true">
                  {m.icon}
                </div>
                <div>
                  <div className="lnd-feat__title">{m.title}</div>
                  <div className="lnd-feat__desc">{m.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="lnd-screens-grid lnd-mt">
            {(content.modules?.screens || []).map((screen) => (
              <ScreenCard
                key={screen.title}
                title={screen.title}
                src={screen.src}
                alt={screen.alt}
              />
            ))}
          </div>
        </div>
      </section>

      <section id="roles" className="lnd-section">
        <div className="lnd-container lnd-grid lnd-grid--2">
          <div>
            <div className="lnd-kicker">{content.roles?.kicker}</div>
            <h2 className="lnd-h2">{content.roles?.title}</h2>

            <div className="lnd-list">
              {(content.roles?.items || []).map((r) => (
                <div key={r.title} className="lnd-list__item">
                  <span className="lnd-check" aria-hidden="true" />
                  <span>
                    <b>{r.title}</b> — {r.desc}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="lnd-screens-grid">
            {(content.roles?.screens || []).map((screen) => (
              <ScreenCard
                key={screen.title}
                title={screen.title}
                src={screen.src}
                alt={screen.alt}
              />
            ))}
          </div>
        </div>
      </section>

      <section id="attendance" className="lnd-section">
        <div className="lnd-container lnd-grid lnd-grid--2">
          <div>
            <div className="lnd-kicker">{content.attendance?.kicker}</div>
            <h2 className="lnd-h2">{content.attendance?.title}</h2>

            {(content.attendance?.paragraphs || []).map((p) => (
              <p key={p} className="lnd-p">
                {p}
              </p>
            ))}
          </div>

          <div className="lnd-screens-grid">
            {(content.attendance?.screens || []).map((screen) => (
              <ScreenCard
                key={screen.title}
                title={screen.title}
                src={screen.src}
                alt={screen.alt}
              />
            ))}
          </div>
        </div>
      </section>

      <section id="tournaments" className="lnd-section">
        <div className="lnd-container lnd-grid lnd-grid--2">
          <div>
            <div className="lnd-kicker">{content.tournaments?.kicker}</div>
            <h2 className="lnd-h2">{content.tournaments?.title}</h2>
            <p className="lnd-p">{content.tournaments?.text}</p>
          </div>

          <div className="lnd-screens-grid">
            {(content.tournaments?.screens || []).map((screen) => (
              <ScreenCard
                key={screen.title}
                title={screen.title}
                src={screen.src}
                alt={screen.alt}
              />
            ))}
          </div>
        </div>
      </section>

      <section id="rating" className="lnd-section">
        <div className="lnd-container lnd-grid lnd-grid--2">
          <div>
            <div className="lnd-kicker">{content.rating?.kicker}</div>
            <h2 className="lnd-h2">{content.rating?.title}</h2>
            <p className="lnd-p">{content.rating?.text}</p>
          </div>

          <div className="lnd-screens-grid">
            {(content.rating?.screens || []).map((screen) => (
              <ScreenCard
                key={screen.title}
                title={screen.title}
                src={screen.src}
                alt={screen.alt}
              />
            ))}
          </div>
        </div>
      </section>

      <section id="analytics" className="lnd-section">
        <div className="lnd-container">
          <div className="lnd-kicker">{content.analytics?.kicker}</div>
          <h2 className="lnd-h2">{content.analytics?.title}</h2>
          <p className="lnd-p">{content.analytics?.text}</p>

          <div className="lnd-grid lnd-grid--4 lnd-mt">
            {analyticsStats.map((x, idx) => (
              <div key={`${x.value || ""}_${x.label || ""}_${idx}`} className="lnd-stat">
                <div className="lnd-stat__t">{x.value || x.title}</div>
                <div className="lnd-stat__d">{x.label || x.desc}</div>
              </div>
            ))}
          </div>

          <div className="lnd-screens-grid lnd-mt">
            {(content.analytics?.screens || []).map((screen) => (
              <ScreenCard
                key={screen.title}
                title={screen.title}
                src={screen.src}
                alt={screen.alt}
              />
            ))}
          </div>
        </div>
      </section>

      <section id="ai" className="lnd-section lnd-ai-section">
        <div className="lnd-container">
          <div className="lnd-kicker">{content.ai?.kicker}</div>
          <h2 className="lnd-h2">{content.ai?.title}</h2>
          <p className="lnd-p lnd-section-intro">{content.ai?.text}</p>
          <div className="lnd-grid lnd-grid--4 lnd-mt">
            {(content.ai?.cards || []).map((item) => (
              <div className="lnd-feat lnd-ai-card" key={item.title}>
                <div className="lnd-feat__icon" aria-hidden="true">{item.icon}</div>
                <div><div className="lnd-feat__title">{item.title}</div><div className="lnd-feat__desc">{item.desc}</div></div>
              </div>
            ))}
          </div>
          <div className="lnd-actions">
            <button className="lnd-btn lnd-btn--primary" type="button" onClick={openBot}>{content.ai?.button}</button>
            <button className="lnd-btn lnd-btn--secondary" type="button" onClick={() => navigate('/clubs/search')}>{content.ai?.searchButton}</button>
          </div>
        </div>
      </section>

      <section id="trial" className="lnd-section lnd-trial-section">
        <div className="lnd-container">
          <div className="lnd-trial">
            <div className="lnd-kicker">{content.trial?.kicker}</div>
            <h2 className="lnd-h2">{content.trial?.title}</h2>
            <p className="lnd-p">{content.trial?.text}</p>
            <div className="lnd-trial__features">
              {(content.trial?.features || []).map((item) => <div key={item} className="lnd-trial__feature"><span className="lnd-check" aria-hidden="true" /><span>{item}</span></div>)}
            </div>
            <div className="lnd-actions"><button type="button" className="lnd-btn lnd-btn--primary" onClick={openBot}>{content.trial?.button}</button></div>
            <p className="lnd-pricing-disclaimer">{content.trial?.note}</p>
          </div>
        </div>
      </section>

      <section id="pricing" className="lnd-section">
        <div className="lnd-container">
          <div className="lnd-kicker">{content.pricing?.kicker}</div>
          <h2 className="lnd-h2">{content.pricing?.title}</h2>
          <p className="lnd-p">{content.pricing?.text}</p>
          <div className="lnd-price-selector">
            <label htmlFor="lnd-price-country">{content.priceUi?.country}</label>
            <select id="lnd-price-country" value={priceCountry} onChange={(e) => setPriceCountry(e.target.value)}>
              {Object.entries(PRICE_COUNTRIES).map(([code, item]) => <option key={code} value={code}>{item.label}</option>)}
            </select>
            {priceCurrency !== "USD" && ratesPending && !usdRates?.[priceCurrency] && <span className="lnd-price-selector__note">{content.priceUi?.fxLoading}</span>}
            {priceCurrency !== "USD" && !ratesPending && !usdRates?.[priceCurrency] && <span className="lnd-price-selector__note">{content.priceUi?.fxNoRate}</span>}
          </div>

          <div className="lnd-grid lnd-grid--4 lnd-mt lnd-pricing-grid">
            {pricingPlans.map((plan) => (
              <div
                key={plan.name}
                className={`lnd-card lnd-card--pad ${plan.highlight ? "lnd-card--highlight" : ""}`}
              >
                <div className="lnd-pricing-top">
                  <div>
                    <div className="lnd-kicker">{plan.name}</div>
                    <div className="lnd-price-main">
                      <h3 className="lnd-h3">{plan.priceUsd === 0 ? content.priceUi?.free : `$${plan.priceUsd ?? plan.price}`}</h3>
                      {plan.priceUsd > 0 && <span>{content.priceUi?.monthly}</span>}
                    </div>
                    {showLocalPrice(plan.priceUsd)}
                  </div>
      
                  {plan.badge ? (
                    <div className="lnd-plan-badge">{plan.badge}</div>
                  ) : null}
                </div>
      
                {plan.description ? (
                  <p className="lnd-p lnd-plan-description">{plan.description}</p>
                ) : null}
      
                <div className="lnd-list">
                  {(plan.features || []).map((item) => (
                    <div key={item} className="lnd-list__item">
                      <span className="lnd-check" aria-hidden="true" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
      
                {plan.notIncluded?.length ? (
                  <div className="lnd-plan-disabled-list">
                    {plan.notIncluded.map((item) => (
                      <div key={item} className="lnd-list__item lnd-list__item--disabled">
                        <span className="lnd-check lnd-check--disabled" aria-hidden="true" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
          {priceCurrency !== "USD" && usdRates?.[priceCurrency] && <p className="lnd-pricing-disclaimer">{content.priceUi?.fxDisclaimer} <a href="https://www.exchangerate-api.com" target="_blank" rel="noopener noreferrer">{content.priceUi?.ratesAttribution}</a>.</p>}
          {content.pricing?.notice ? <p className="lnd-pricing-disclaimer">{content.pricing.notice}</p> : null}
        </div>
      </section>

      <section id="packages" className="lnd-section lnd-ai-packs">
        <div className="lnd-container">
          <div className="lnd-kicker">{content.packages?.kicker}</div>
          <h2 className="lnd-h2">{content.packages?.title}</h2>
          <p className="lnd-p lnd-section-intro">{content.packages?.text}</p>
          <div className="lnd-grid lnd-grid--3 lnd-mt">
            {(content.packages?.cards || []).map((pack) => (
              <div className="lnd-card lnd-card--pad lnd-pack-card" key={pack.name}>
                <div className="lnd-pack-card__name">{pack.name}</div>
                <div className="lnd-pack-card__price">{pack.priceUsd != null ? `$${pack.priceUsd}` : pack.price}</div>
                {showLocalPrice(pack.priceUsd)}
                <div className="lnd-feat__desc">{pack.detail}</div>
              </div>
            ))}
          </div>
          <p className="lnd-pricing-disclaimer">{content.packages?.note}</p>
          <div className="lnd-actions"><a className="lnd-btn lnd-btn--secondary lnd-anchor-btn" href="https://t.me/club1_support" target="_blank" rel="noopener noreferrer">{content.packages?.button}</a></div>
        </div>
      </section>

      <section id="referral" className="lnd-section lnd-referral-section">
        <div className="lnd-container">
          <div className="lnd-referral">
            <div className="lnd-kicker">{content.referral?.kicker}</div>
            <h2 className="lnd-h2">{content.referral?.title}</h2>
            <p className="lnd-p">{content.referral?.text}</p>
            <div className="lnd-referral__steps">
              {(content.referral?.steps || []).map((step, index) => (
                <div className="lnd-referral__step" key={step}><span className="lnd-referral__num">{index+1}</span><span>{step}</span></div>
              ))}
            </div>
            <p className="lnd-referral__note">{content.referral?.note}</p>
            <div className="lnd-actions"><a className="lnd-btn lnd-btn--primary lnd-anchor-btn" href="https://t.me/club1_support" target="_blank" rel="noopener noreferrer">{content.referral?.button}</a></div>
          </div>
        </div>
      </section>

      <section id="faq" className="lnd-section">
        <div className="lnd-container">
          <div className="lnd-kicker">{content.faq?.kicker}</div>
          <h2 className="lnd-h2">{content.faq?.title}</h2>

          <div className="lnd-faq lnd-mt">
            {(content.faq?.items || []).map((item) => (
              <details key={item.q} className="lnd-details">
                <summary className="lnd-summary">{item.q}</summary>
                <div className="lnd-details__body">
                  <HtmlDiv content={item.a}/>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="lnd-section">
        <div className="lnd-container">
          <div className="lnd-card lnd-card--pad lnd-cta">
            <div className="lnd-kicker">{content.cta?.kicker}</div>
            <h2 className="lnd-h2">{content.cta?.title}</h2>
            <p className="lnd-p">{content.cta?.text}</p>
            <div className="lnd-actions">
              <button
                type="button"
                className="lnd-btn lnd-btn--primary"
                onClick={openBot}
              >
                {content.cta?.button}
              </button>
            </div>
          </div>
        </div>
      </section>

      <footer className="lnd-footer">
        <div className="lnd-container lnd-footer__inner">
          <div>
            <div className="lnd-footer__brand">{content.footer?.brand}</div>
            <div className="lnd-muted">{content.footer?.text}</div>
          </div>
          <div className="lnd-muted">© {new Date().getFullYear()} Club #1</div>
        </div>
      </footer>
    </div>
  );
}